import { useEffect, useState } from "react";
import { ClipboardList, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { GOOGLE_REVIEW_URL } from "@/data/site";
import { VisitReportItem } from "@/components/VisitReportItem";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const RecordItem = ({ record }) => (
  <li data-testid={`service-record-${record.id}`} className="py-4 first:pt-0 last:pb-0">
    <div className="flex flex-wrap items-center justify-between gap-2">
      <p className="font-medium text-brand-ink">{record.service}</p>
      <span className={`text-xs font-bold px-2.5 py-1 rounded-sm uppercase tracking-wide ${record.status === "scheduled" ? "bg-brand-ice/30 text-blue-900" : "bg-brand-subtle text-brand"}`}>
        {record.status}
      </span>
    </div>
    <p className="text-xs text-zinc-500 mt-1">{record.date}</p>
    {record.notes && <p className="text-sm text-zinc-600 mt-1">{record.notes}</p>}
    {record.status === "completed" && (
      <a href={GOOGLE_REVIEW_URL} target="_blank" rel="noopener noreferrer" data-testid={`google-review-btn-${record.id}`}
        className="inline-flex items-center gap-1.5 mt-2 border-2 border-brand text-brand hover:bg-brand hover:text-white font-bold px-4 py-2 rounded-sm text-xs transition-colors duration-200">
        Leave Us a Google Review
      </a>
    )}
  </li>
);

export const ServiceHistory = () => {
  const [data, setData] = useState(null);
  const [locationFilter, setLocationFilter] = useState("all");

  useEffect(() => {
    api.get("/account/service-history").then((r) => setData(r.data))
      .catch(() => setData({ records: [], visits: [], locations: [] }));
  }, []);

  let body;
  if (data === null) {
    body = <Loader2 className="w-6 h-6 text-brand animate-spin" />;
  } else {
    const visits = locationFilter === "all"
      ? data.visits
      : data.visits.filter((v) => v.location?.id === locationFilter);
    const records = locationFilter === "all" ? data.records : [];
    if (visits.length === 0 && records.length === 0) {
      body = (
        <p data-testid="service-history-empty" className="text-sm text-zinc-600">
          No service records{locationFilter !== "all" ? " for this location" : ""} yet. Once you book your first visit, your history will appear here.
        </p>
      );
    } else {
      body = (
        <ul className="divide-y divide-zinc-100">
          {visits.map((v) => <VisitReportItem key={v.id} visit={v} />)}
          {records.map((r) => <RecordItem key={r.id} record={r} />)}
        </ul>
      );
    }
  }

  return (
    <div data-testid="service-history-card" className="bg-white border border-zinc-200 p-6 rounded-sm">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <h2 className="font-heading font-bold text-lg text-brand-ink flex items-center gap-2">
          <ClipboardList className="w-5 h-5 text-brand" /> Service History
        </h2>
        {data?.locations?.length > 1 && (
          <Select value={locationFilter} onValueChange={setLocationFilter}>
            <SelectTrigger data-testid="service-history-location-filter" className="rounded-sm h-9 w-48 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All locations</SelectItem>
              {data.locations.map((l) => <SelectItem key={l.id} value={l.id}>{l.label}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
      </div>
      {body}
    </div>
  );
};
